import { useState, useEffect } from 'react';
import {
    collection,
    onSnapshot,
    query,
    orderBy,
    addDoc,
    updateDoc,
    doc,
    Timestamp,
    arrayUnion,
    increment
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface ChatMessage {
    id: string;
    roomId: string;
    senderId: string;
    senderName: string;
    senderRole: 'admin' | 'user';
    message: string;
    readBy: string[];
    createdAt: Timestamp;
}

export const useChatMessages = (roomId?: string) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!roomId) {
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, 'chatRooms', roomId, 'messages'),
            orderBy('createdAt', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as ChatMessage[];
            setMessages(data);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching messages:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [roomId]);

    const sendMessage = async (
        roomId: string,
        message: string,
        senderId: string,
        senderName: string,
        senderRole: 'admin' | 'user'
    ) => {
        try {
            console.log('🟢 [CHAT] Sending message...', { roomId, message, senderId, senderRole });

            // Add message to subcollection
            await addDoc(collection(db, 'chatRooms', roomId, 'messages'), {
                roomId,
                senderId,
                senderName,
                senderRole,
                message,
                readBy: [senderId], // Sender has read it
                createdAt: Timestamp.now()
            });

            // Update chat room with last message and increment unread count
            const unreadField = senderRole === 'admin' ? 'unreadCountUser' : 'unreadCountAdmin';
            await updateDoc(doc(db, 'chatRooms', roomId), {
                lastMessage: message,
                lastMessageTime: Timestamp.now(),
                [unreadField]: increment(1)
            });

            console.log('✅ [CHAT] Message sent successfully');
            return { success: true };
        } catch (error) {
            console.error('❌ [CHAT] Error sending message:', error);
            return { success: false, error };
        }
    };

    const markMessageAsRead = async (roomId: string, messageId: string, userId: string) => {
        try {
            await updateDoc(doc(db, 'chatRooms', roomId, 'messages', messageId), {
                readBy: arrayUnion(userId)
            });
            return { success: true };
        } catch (error) {
            console.error('Error marking message as read:', error);
            return { success: false, error };
        }
    };

    return {
        messages,
        loading,
        sendMessage,
        markMessageAsRead
    };
};
